import { CONSTANTS } from 'src/components/Constants';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BarChartIcon from '@mui/icons-material/BarChart';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

/**
 * @typedef {Object} QuickAction
 * @property {string} key            - Unique identifier
 * @property {string} label          - Display label (English; translated via i18n)
 * @property {string|null} path      - Navigate to this path (null for custom handlers)
 * @property {string} iconColor      - Badge icon colour (legacy home bar only)
 * @property {React.ComponentType} [Icon] - Icon for the global quick-actions menu
 * @property {string[]} [requiredRoles]   - Restrict to these userTypes
 * @property {string} [requiredFeature]   - systemFeatures key gating this action
 */

/**
 * @typedef {Object} QuickActionGroup
 * @property {string} id
 * @property {string} label          - Group heading (English; translated via i18n)
 * @property {QuickAction[]} actions
 */

// systemFeatures key that gates the whole global quick-actions menu. It IS in
// featureGating's DEFAULT_ON set, so it resolves truthy unless a deployment sets it
// to false explicitly. Shared by the menu and the home-bar retirement gate.
export const QUICK_ACTIONS_MENU_FLAG = 'FEATURE_GLOBAL_QUICK_ACTIONS';

// Action definitions — the single source of truth. `Icon` is consumed by the
// global menu; the legacy home bar (admin/home/QuickActions.jsx) maps key -> icon
// itself and ignores it.
const findPerson = {
  key: 'findPerson',
  label: 'Find person',
  // TODO: trigger global search (⌘K) once a search modal is implemented.
  // For now, navigates to the Search Family History page.
  path: CONSTANTS.probandInfoPath + '?focus=search',
  iconColor: '#f4d257',
  requiredFeature: 'FEATURE_FAMILY_RECORD_MANAGEMENT',
  Icon: PersonSearchIcon
};

const newPedigree = {
  key: 'newPedigree',
  label: 'New pedigree',
  path: CONSTANTS.pedigreeToolPath + '?action=add',
  iconColor: '#4b95a8',
  Icon: AccountTreeIcon
};

const importPedigree = {
  key: 'importPedigree',
  label: 'Import pedigree',
  path: CONSTANTS.pedigreeToolPath + '?action=import',
  iconColor: '#4b95a8',
  Icon: UploadFileIcon
};

const sendQuestionnaire = {
  key: 'sendQuestionnaire',
  label: 'Send questionnaire',
  path: CONSTANTS.generateLinkPath + '?focus=email',
  iconColor: '#2e3747',
  requiredFeature: 'FEATURE_FAMILY_HISTORY_QUESTIONNAIRE',
  Icon: FamilyRestroomIcon
};

const sendForm = {
  key: 'sendForm',
  label: 'Send form',
  // Custom-form generate-links page in the form builder
  // (formCreationPath is /admin/form-builder).
  path: CONSTANTS.formCreationPath + '/generate-links',
  // Red, matching the "Forms" home tile.
  iconColor: '#d2214c',
  // Only meaningful where custom forms exist, so gate on the form-builder feature.
  requiredFeature: 'FEATURE_FORM_BUILDER',
  // Matches the "Forms" home tile icon.
  Icon: AssignmentIcon
};

const runReport = {
  key: 'runReport',
  label: 'Run report',
  path: CONSTANTS.dataExplorer,
  iconColor: '#54a978',
  requiredFeature: 'FEATURE_DATA_EXPLORER',
  Icon: BarChartIcon
};

const manageUsers = {
  key: 'manageUsers',
  label: 'Manage users',
  path: CONSTANTS.manageUserPath,
  iconColor: '#888780',
  requiredRoles: ['admin', 'genetics', 'geneticsServiceAdmin'],
  Icon: ManageAccountsIcon
};

// Legacy flat list — consumed by the home-page Quick actions bar. Unchanged
// (same six actions, same order) so that, with the menu flag off, the home bar
// renders exactly as before.
/** @type {QuickAction[]} */
const QUICK_ACTIONS = [
  findPerson,
  newPedigree,
  sendQuestionnaire,
  importPedigree,
  runReport,
  manageUsers
];

// Grouped structure — consumed by the global top-bar menu (and any future
// surface such as a command palette). Adds "Send form"; drops nothing.
/** @type {QuickActionGroup[]} */
export const QUICK_ACTION_GROUPS = [
  {
    id: 'patients',
    label: 'Patients and families',
    actions: [findPerson, newPedigree, importPedigree]
  },
  {
    id: 'clinic',
    label: 'Clinic',
    actions: [sendQuestionnaire, sendForm]
  },
  {
    id: 'insights',
    label: 'Insights',
    actions: [runReport]
  },
  {
    id: 'admin',
    label: 'Admin',
    actions: [manageUsers]
  }
];

export default QUICK_ACTIONS;
